/**
 * Today's Workout Component
 *
 * Shows the athlete's current workout with comprehensive tracking.
 * Focused, actionable view for daily training.
 * Uses WorkoutTracker for detailed exercise logging.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMyAssignments,
  listWorkoutLogs,
  type Organization,
  type MyAssignment,
  type WorkoutLog,
  type Module,
} from '@tss/shared';
import { Flame, Target, Dumbbell, Play, CheckCircle } from 'lucide-react';
import { WorkoutTracker } from './WorkoutTracker';

interface TodayWorkoutProps {
  org: Organization | null;
  proxyAthleteId?: string; // For guardian proxy mode
}

export function TodayWorkout({ org, proxyAthleteId }: TodayWorkoutProps) {
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<MyAssignment | null>(null);
  const [streakDays, setStreakDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completedCount, setCompletedCount] = useState(0);
  const [showWorkoutTracker, setShowWorkoutTracker] = useState(false);
  const [expandCompleted, setExpandCompleted] = useState(false);

  useEffect(() => {
    if (org) {
      loadTodayWorkout();
    } else {
      setLoading(false);
    }
  }, [org]);

  async function loadTodayWorkout() {
    if (!org) return;
    setLoading(true);
    try {
      const data = await getMyAssignments(org.org_id, proxyAthleteId);
      // Get the first active assignment (could enhance to pick most relevant)
      if (data.assignments.length > 0) {
        const currentAssignment = data.assignments[0];
        setAssignment(currentAssignment);

        // Load existing workout logs to get completion count
        if (currentAssignment.current_module) {
          try {
            const logsData = await listWorkoutLogs(currentAssignment.program_id, {
              module_id: currentAssignment.current_module.module_id,
              athlete_id: proxyAthleteId,
            });

            // Count completed or skipped drills (both count as "done")
            const completed = (logsData.workout_logs || []).filter(
              (log: WorkoutLog) => log.status === 'COMPLETED' || log.status === 'SKIPPED'
            ).length;
            setCompletedCount(completed);
          } catch (err) {
            console.error('Failed to load workout logs:', err);
          }
        }
      }
      setStreakDays(data.streak_days);
    } catch (err) {
      console.error('Failed to load today workout:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <div
          className="animate-spin"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: '3px solid var(--gray-200)',
            borderTopColor: 'var(--purple-600)',
            margin: '0 auto',
          }}
        />
      </div>
    );
  }

  if (!assignment || !assignment.current_module) {
    return (
      <div
        className="card"
        style={{
          padding: '32px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
        }}
      >
        <div
          className="icon-container icon-container-lg"
          style={{ background: 'var(--gray-200)', margin: '0 auto 16px' }}
        >
          <Dumbbell size={28} style={{ color: 'var(--gray-500)' }} />
        </div>
        <h3
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--gray-800)',
            marginBottom: '8px',
          }}
        >
          No workout assigned
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '16px' }}>
          Ask your coach to assign you a training program
        </p>
        <button
          className="btn-secondary"
          onClick={() => navigate('/my-programs')}
          style={{ fontSize: '14px' }}
        >
          View Programs
        </button>
      </div>
    );
  }

  const module = assignment.current_module;
  const allDrills = module.drills || [];
  const warmup = module.warmup_exercises || [];
  const cooldown = module.cooldown_exercises || [];
  const totalItems = warmup.length + allDrills.length + cooldown.length;
  const progressPercent = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;
  const isWorkoutComplete = totalItems > 0 && completedCount >= totalItems;

  // Convert the current_module to a Module type for WorkoutTracker.
  //
  // CurrentModule (the API shape powering this view) and Module (what
  // WorkoutTracker takes) describe *the same data* with two divergences:
  //   - description: CurrentModule lets it be undefined; Module is string|null
  //   - drills[].duration: CurrentModule has `string` (e.g. "5 min");
  //     DrillData (Module's drill shape) has `number` (legacy: minutes).
  // WorkoutTracker treats drills as opaque DrillData and only reads
  // duration_seconds at runtime, so coerce the type via unknown rather
  // than rewriting every drill.
  const moduleForTracker: Module = {
    module_id: module.module_id,
    program_id: assignment.program_id,
    order: module.order,
    title: module.title,
    description: module.description ?? null,
    content: null,
    drills: allDrills as unknown as (string | import('@tss/shared').DrillData)[],
    warmup_exercises: warmup,
    cooldown_exercises: cooldown,
    video_ids: [],
    tags: [],
    duration_estimate: null,
  };

  // Show WorkoutTracker when active
  if (showWorkoutTracker && org) {
    return (
      <WorkoutTracker
        programId={assignment.program_id}
        module={moduleForTracker}
        orgId={org.org_id}
        proxyAthleteId={proxyAthleteId}
        onComplete={() => {
          setShowWorkoutTracker(false);
          loadTodayWorkout();
        }}
        onClose={() => {
          setShowWorkoutTracker(false);
          loadTodayWorkout(); // Reload progress when closing
        }}
      />
    );
  }

  // Show compact completed state when workout is done (unless expanded)
  if (isWorkoutComplete && !expandCompleted) {
    return (
      <div
        className="card animate-fade-in"
        style={{
          padding: '16px',
          background:
            'linear-gradient(135deg, rgba(var(--green-rgb), 0.08) 0%, rgba(var(--green-rgb), 0.02) 100%)',
          border: '1px solid rgba(var(--green-rgb), 0.2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(var(--green-rgb), 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CheckCircle size={22} style={{ color: 'var(--color-success)' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '15px' }}>
                Today's Workout Complete!
              </p>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>
                {module.title} • {totalItems} exercises done
                {streakDays > 0 && (
                  <span style={{ marginLeft: '8px', color: '#f59e0b' }}>
                    <Flame
                      size={12}
                      style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }}
                    />
                    {streakDays} day streak
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => setExpandCompleted(true)}
            className="btn-ghost"
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              color: 'var(--gray-600)',
              flexShrink: 0,
            }}
          >
            Review
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card animate-fade-in" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header with gradient */}
      <div
        style={{
          background: 'linear-gradient(135deg, var(--primary-start) 0%, var(--primary-end) 100%)',
          color: 'white',
          padding: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '10px',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '16px',
              fontSize: '11px',
              fontWeight: '600',
            }}
          >
            <Target size={12} />
            Today's Workout
          </span>
          {streakDays > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 8px',
                background: 'rgba(251, 146, 60, 0.3)',
                borderRadius: '16px',
                fontSize: '11px',
                fontWeight: '600',
              }}
            >
              <Flame size={12} style={{ color: 'var(--gold-400)' }} />
              {streakDays} day streak
            </span>
          )}
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px', color: 'white' }}>
          {module.title}
        </h2>
        <p style={{ fontSize: '12px', opacity: 0.8 }}>
          {assignment.program_title} - Module {module.order} of {assignment.total_modules}
        </p>
      </div>

      {/* Progress bar */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)' }}>
        <div className="flex-between" style={{ marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', color: 'var(--gray-600)' }}>
            {completedCount} of {totalItems} completed
          </span>
          <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--purple-600)' }}>
            {progressPercent}%
          </span>
        </div>
        <div
          style={{
            height: '5px',
            background: 'var(--gray-100)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: 'linear-gradient(90deg, var(--purple-600) 0%, var(--color-success) 100%)',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Start Workout Button */}
      <div style={{ padding: '16px' }}>
        <button
          onClick={() => setShowWorkoutTracker(true)}
          style={{
            width: '100%',
            padding: '14px 20px',
            background: isWorkoutComplete
              ? 'linear-gradient(135deg, var(--color-success) 0%, #16a34a 100%)'
              : 'linear-gradient(135deg, var(--purple-600) 0%, var(--purple-700) 100%)',
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: isWorkoutComplete
              ? '0 4px 12px rgba(var(--green-rgb), 0.3)'
              : '0 4px 12px rgba(59, 130, 246, 0.3)',
          }}
        >
          {isWorkoutComplete ? <CheckCircle size={18} /> : <Play size={18} />}
          {isWorkoutComplete
            ? 'Workout Complete'
            : completedCount > 0
              ? 'Continue Workout'
              : 'Start Workout'}
        </button>

        {/* Module summary */}
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            background: 'var(--gray-50)',
            borderRadius: '8px',
          }}
        >
          <p style={{ fontSize: '12px', color: 'var(--gray-600)', marginBottom: '6px' }}>
            This workout includes:
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {warmup.length > 0 && (
              <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                {warmup.length} warm-up
              </span>
            )}
            {allDrills.length > 0 && (
              <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                {allDrills.length} drill{allDrills.length !== 1 ? 's' : ''}
              </span>
            )}
            {cooldown.length > 0 && (
              <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                {cooldown.length} cool-down
              </span>
            )}
          </div>
        </div>

        {/* Collapse button when complete */}
        {isWorkoutComplete && (
          <button
            onClick={() => setExpandCompleted(false)}
            className="btn-ghost"
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '8px',
              fontSize: '13px',
              color: 'var(--gray-500)',
            }}
          >
            Collapse
          </button>
        )}
      </div>
    </div>
  );
}
